import type {
	ConstantsTeamResponse,
	ConstantsTeamUserInviteStruct,
} from "@gosaas/types";
import { IconLoader, IconUserPlus, IconUsers } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { PageHeader } from "@/components/page-header";
import { PageLayout } from "@/components/page-layout";
import { useTeam } from "@/components/providers/team";
import { useFetchUserInvites } from "@/components/queries/user";
import {
	userInviteColumns,
	userTeamColumns,
} from "@/components/tables/columns";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { DataTable } from "@/components/ui/data-table";
import { redirectIfModuleDisabled } from "@/lib/module-guards";

export const Route = createFileRoute("/_authorized/user/teams")({
	beforeLoad: () => redirectIfModuleDisabled("team"),
	component: RouteComponent,
});

export function RouteComponent() {
	const { teams, loading: isLoading } = useTeam();
	const { userInvites, isLoadingInvites } = useFetchUserInvites();

	if (isLoadingInvites) return <IconLoader />;
	return (
		<PageLayout>
			<PageHeader
				breadcrumbItems={[
					{ title: "User", href: null },
					{ title: "Teams", href: "/user/teams" },
				]}
				title="Teams"
				description="Your teams and pending invitations"
				icon={<IconUsers className="size-6 text-primary" aria-hidden />}
			/>

			<Card className="shadow-sm">
				<CardHeader className="pb-4">
					<div className="flex items-center gap-3">
						<div className="p-2 bg-blue-500/10 rounded-lg">
							<IconUsers className="h-5 w-5 text-blue-500" />
						</div>
						<div>
							<CardTitle className="text-xl">Teams</CardTitle>
							<p className="text-sm text-muted-foreground mt-1">
								View all teams you are a member of.
							</p>
						</div>
					</div>
				</CardHeader>
				<CardContent className="pt-0">
					<DataTable
						columns={userTeamColumns}
						data={teams as ConstantsTeamResponse[]}
						loading={isLoading}
						filterColumn="role"
						hiddenColumns={["joined_at"]}
						usePagination={true}
						pageSize={5}
					/>
				</CardContent>
			</Card>

			<Card className="shadow-sm">
				<CardHeader className="pb-4">
					<div className="flex items-center gap-3">
						<div className="p-2 bg-green-500/10 rounded-lg">
							<IconUserPlus className="h-5 w-5 text-green-600" />
						</div>
						<div>
							<CardTitle className="text-xl">Team Invites</CardTitle>
							<CardDescription>
								View all teams you are invited to.
							</CardDescription>
						</div>
					</div>
				</CardHeader>
				<CardContent className="pt-0">
					<DataTable
						columns={userInviteColumns}
						data={userInvites as ConstantsTeamUserInviteStruct[]}
						loading={isLoadingInvites}
						filterColumn="role"
						hiddenColumns={["joined_at"]}
						usePagination={true}
						pageSize={5}
					/>
				</CardContent>
			</Card>
		</PageLayout>
	);
}
